import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api.get(`/posts/${slug}`)
      .then((r) => { setPost(r.data); setStatus("ok"); })
      .catch(() => setStatus("missing"));
  }, [slug]);

  if (status === "loading") {
    return <div className="pt-40 pb-32 text-center text-slate-500" data-testid="post-loading">Loading article...</div>;
  }

  if (status === "missing") {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="post-not-found">
        <h1 className="font-display text-3xl font-bold text-forest">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-leaf-hover font-semibold underline underline-offset-2">Back to the blog</Link>
      </div>
    );
  }

  return (
    <div>
      <Seo
        title={post.seo_title || `${post.title} | Glow Green`}
        description={post.seo_description || post.excerpt}
        image={post.image}
      />
      <article className="pt-32 lg:pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Reveal>
            <Link to="/blog" data-testid="post-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-leaf-hover hover:gap-3 transition-all">
              <ArrowLeft className="w-4 h-4" /> All articles
            </Link>
            <div className="mt-6 flex items-center gap-3 text-xs">
              <span className="rounded-full bg-mint px-3 py-1 font-semibold text-forest">{post.category}</span>
              <time dateTime={post.published_at} className="text-slate-400">{format(new Date(post.published_at), "MMMM d, yyyy")}</time>
            </div>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-forest leading-[1.1]" data-testid="post-title">
              {post.title}
            </h1>
          </Reveal>
        </div>
        {post.image && (
          <Reveal delay={0.1} className="max-w-5xl mx-auto px-4 sm:px-8 mt-10">
            <img src={post.image} alt={post.image_alt || post.title} className="w-full aspect-[21/9] object-cover rounded-2xl clipped-frame" />
          </Reveal>
        )}
        <Reveal delay={0.15} className="max-w-3xl mx-auto px-4 sm:px-8 mt-10">
          <div className="prose-gg" data-testid="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        </Reveal>
      </article>

      <section className="bg-forest grain relative py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 relative z-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Ready for a brighter clean?</h2>
          <p className="mt-3 text-white/70">Get a free, tailored quote for your home or business.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="post-quote-cta" className="inline-flex items-center gap-2 rounded-full bg-leaf px-6 py-3 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03]">
              Request a Free Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/residential-cleaning" data-testid="post-residential-cta" className="inline-flex items-center rounded-full border border-white/25 px-6 py-3 font-display font-bold text-sm text-white transition-colors hover:border-leaf hover:text-leaf">
              Residential Cleaning
            </Link>
            <Link to="/commercial-cleaning" data-testid="post-commercial-cta" className="inline-flex items-center rounded-full border border-white/25 px-6 py-3 font-display font-bold text-sm text-white transition-colors hover:border-leaf hover:text-leaf">
              Commercial Cleaning
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
